import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule  } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FaqService } from '../service/faq.service';
import { PropertyService } from '../service/property.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
interface FAQItem {
  _id?: string;      // optional because new items may not have it yet
  question: string;
  answer: string;
}

interface FAQGroup {
  _id?: string;          // MongoDB ID of the group
  title: string;
  faqs: FAQItem[];
  collapsed: boolean;    // required for UI
  isEditingTitle?: boolean; // frontend-only flag
}

@Component({
  selector: 'app-faq-management',
  templateUrl: './faq-management.component.html',
  styleUrls: ['./faq-management.component.scss'],
  standalone: true,
  imports: [ButtonModule,InputTextModule ,InputTextareaModule,FormsModule,CommonModule,ToastModule,DialogModule],
  providers: [MessageService],
})
export class FaqManagementComponent {
  groups: FAQGroup[] = [];
  @ViewChildren('groupCard') groupCards!: QueryList<ElementRef>;
  @ViewChildren('faqRow') faqRows!: QueryList<ElementRef>;
  propertyId: string = '';
  propertyTimezone: string = '';
  showDeleteGroupDialog: boolean = false;
  groupToDelete: any = null;
  groupIndexToDelete: number | null = null;
  showDeleteGroupFaqDialog: boolean = false;
  faqGroupToDelete: FAQGroup | null = null;
  faqIndexToDelete: number | null = null;
  constructor(
    private faqService: FaqService, 
    private propertyService: PropertyService,
    private messageService: MessageService
  ){}

  ngOnInit() {
    this.propertyService.getUserInfo().subscribe(user => {
      this.propertyId = user.property_id;
      this.propertyTimezone = user.property.timezone;
      this.loadGroups();
    });
  }

  loadGroups() {
    this.faqService.getGroups(this.propertyId).subscribe((groups) => {
      this.groups = groups.map(g => ({
        ...g,
        collapsed: g.collapsed ?? false, // ensure boolean
        isEditingTitle: false
      }));
    });
  }

  // ADD NEW GROUP
  addGroup() {
    this.groups.push({
      title: '',
      isEditingTitle: true, // show input by default
      collapsed: false,
      faqs: [
        { question: '', answer: '' } // default 1 row
      ]
    });

    // Scroll to the new group after DOM updates
    setTimeout(() => {
      const lastCard = this.groupCards.last;
      if (lastCard) {
        lastCard.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',   // 👈 smoother than "start"
          inline: 'nearest'
        });
        //this.smoothScrollTo(lastCard.nativeElement, 700);
      }
    }, 100); // small delay to ensure DOM is updated
  }

  // SAVE TITLE
  saveGroupTitle(group: FAQGroup) {
    if (!group.title?.trim()) return; // optional validation

    // If group already has _id, update title
    if (group._id) {
      this.faqService.updateGroupTitle(group._id, group.title).subscribe(updated => {
        group.title = updated.title;
        group.isEditingTitle = false;
      });
    } else {
      // New group → call createGroup service
      this.faqService.createGroup(this.propertyId, group.title).subscribe(createdGroup => {
        group._id = createdGroup._id;
        group.isEditingTitle = false;

        // Map FAQ rows to backend _id after creation if backend returns them
        if (createdGroup.faqs && createdGroup.faqs.length) {
          group.faqs = createdGroup.faqs;
        }
      });
    }
  }
  saveGroupTitle2(group: FAQGroup) {
    group.isEditingTitle = false;
  }

  // ENABLE TITLE EDIT
  editGroupTitle(group: FAQGroup) {
    group.isEditingTitle = true;
    setTimeout(() => {
      const input: any = document.querySelector(`#group-title-${this.groups.indexOf(group)}`);
      if (input) input.focus();
    });
  }
  editGroupTitle2(group: FAQGroup, index: number) {
    group.isEditingTitle = true;
    setTimeout(() => {
    const input: any = document.querySelector(`#group-title-${index}`);
    if (input) input.focus();
  });
  }

  // ADD FAQ ROW
  addFaq2(group: FAQGroup) {
    if (!group._id) return;

    this.faqService.addFaqItem(group._id, '', '').subscribe(updatedGroup => {
      const gIndex = this.groups.findIndex(g => g._id === group._id);

      this.groups[gIndex] = {
        ...updatedGroup,
        collapsed: updatedGroup.collapsed ?? false, // ensure boolean
        isEditingTitle: group.isEditingTitle
      };
    });
  }
  addFaq(group: FAQGroup) {
    group.faqs.push({ question: '', answer: '' });
    // Scroll to the newly added FAQ
    
    setTimeout(() => {
      // Filter FAQ rows that belong to this group
      const groupIndex = this.groups.indexOf(group);
      if (groupIndex === -1) return;

      // Count total FAQs before this group to find the correct row in DOM
      let faqCountBeforeGroup = 0;
      for (let i = 0; i < groupIndex; i++) {
        faqCountBeforeGroup += this.groups[i].faqs.length;
      }

      const lastFaqIndex = faqCountBeforeGroup + group.faqs.length - 1;
      const faqElement = this.faqRows.toArray()[lastFaqIndex];
      if (faqElement) {
        faqElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100); // wait for DOM update
  }

  createOrUpdateFaqItem(group: FAQGroup, item: FAQItem) {
    // 🔹 Group must exist in DB first
    if (!group._id) return;

    const gIndex = this.groups.findIndex(g => g._id === group._id);

    // 🔹 CREATE FAQ ITEM (no _id yet)
    if (!item._id) {
      this.faqService
        .addFaqItem(group._id, item.question, item.answer)
        .subscribe(updatedGroup => {
          this.groups[gIndex] = {
            ...updatedGroup,
            collapsed: updatedGroup.collapsed ?? false,
            isEditingTitle: group.isEditingTitle
          };
        });

      return;
    }

    // 🔹 UPDATE FAQ ITEM (has _id)
    this.faqService
      .updateFaqItem(group._id, item._id, item.question, item.answer)
      .subscribe(updatedGroup => {
        this.groups[gIndex] = {
          ...updatedGroup,
          collapsed: updatedGroup.collapsed ?? false,
          isEditingTitle: group.isEditingTitle
        };
      });
  }

  saveGroupFaqs(group: FAQGroup) {
    if (!group._id) {
      this.faqService.createGroup(this.propertyId, group.title)
        .subscribe(createdGroup => {
          group._id = createdGroup._id;
          group.isEditingTitle = false;
          this.saveGroupFaqsItems(group);
        });
      return;
    }

    this.saveGroupFaqsItems(group);
  }

  saveGroupFaqsItems(group: FAQGroup) {
    const faqs = group.faqs.filter(
      f => f.question?.trim()
    );

    // 🚫 Prevent empty save
    if (!faqs.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please add at least one FAQ with question & answer.',
      });
      return;
    }

    const payload = {
      title: group.title,
      faqs
    };

    this.faqService.createOrUpdateFaqs(group._id!, payload).subscribe({
      next: (updatedGroup) => {
        const index = this.groups.findIndex(g => g._id === group._id);
        if (index === -1) return;

        this.groups[index] = {
          _id: updatedGroup._id,
          title: updatedGroup.title,
          faqs: updatedGroup.faqs,
          collapsed: group.collapsed ?? false,
          isEditingTitle: false
        };

        // ✅ SUCCESS MESSAGE
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Group FAQs are saved successfully!',
        });
      },
      error: (error) => {
        const errorMessage =
          error?.error?.message || 'Failed to save group FAQs!';

        // ❌ ERROR MESSAGE
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      }
    });
  }

  // DELETE FAQ ROW (except 0th index)
  deleteFaq(group: FAQGroup, itemIndex: number) {
    const item = group.faqs[itemIndex];
    if (!group._id || !item._id) {
      this.deleteFaq2(group, itemIndex);
      this.messageService.add({
        severity: 'success',
        summary: 'Deleted',
        detail: 'FAQ deleted successfully'
      });
      return;
    }

    this.faqService.deleteFaqItem(group._id, item._id).subscribe({
      next: updatedGroup => {
        const gIndex = this.groups.findIndex(g => g._id === group._id);

        this.groups[gIndex] = {
          ...updatedGroup,
          collapsed: updatedGroup.collapsed ?? false,
          isEditingTitle: group.isEditingTitle
        };

        // ✅ Success message
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'FAQ deleted successfully'
        });
      },
      error: () => {
        // ❌ Error message
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete FAQ'
        });
      }
    });
  }

  deleteFaq2(group: FAQGroup, index: number) {
    if (index === 0) return; // prevent delete first
    group.faqs.splice(index, 1);
  }

  // TOGGLE COLLAPSE
  toggleCollapse(group: FAQGroup) {
    group.collapsed = !group.collapsed;
  }

  deleteGroup(group: any, index: number) {
    if (!group || !group._id) return;

    // Optional: confirm deletion
    if (!confirm(`Are you sure you want to delete group "${group.title}"?`)) return;

    // Call backend API to soft delete
    this.faqService.softDeleteGroup(group._id).subscribe({
      next: (res) => {
        // Optionally show toast
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `Group "${group.title}" deleted` });

        // Remove group from frontend list (optional)
        this.groups.splice(index, 1)
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not delete group' });
        console.error(err);
      }
    });
  }

  // Show dialog when trash icon clicked
  showDeleteDialog(group: any, index: number) {
    this.groupToDelete = group;
    this.showDeleteGroupDialog = true;
    this.groupIndexToDelete = index;
  }

  // Cancel deletion
  cancelDelete() {
    this.groupToDelete = null;
    this.showDeleteGroupDialog = false;
    this.groupIndexToDelete = null;
  }

  // Confirm deletion
  confirmDelete() {
    if (this.groupIndexToDelete === null || !this.groupToDelete) {
      return;
    }
    
    // 🆕 NEW GROUP → LOCAL DELETE ONLY
    if (!this.groupToDelete._id) {
      this.groups.splice(this.groupIndexToDelete, 1);

      this.messageService.add({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Group deleted successfully',
      });

      this.cancelDelete();
      return;
    }

    this.faqService.softDeleteGroup(this.groupToDelete._id).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: `Group "${this.groupToDelete.title}" deleted`
        });

        // Remove from UI list
        this.groups = this.groups.filter(g => g._id !== this.groupToDelete._id);

        // Close dialog
        this.cancelDelete();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not delete group'
        });
        console.error(err);
      }
    });
  }

  openDeleteFaqDialog(group: FAQGroup, index: number) {
    this.faqGroupToDelete = group;
    this.faqIndexToDelete = index;
    this.showDeleteGroupFaqDialog = true;
  }

  cancelDeleteFaq() {
    this.showDeleteGroupFaqDialog = false;
    this.faqGroupToDelete = null;
    this.faqIndexToDelete = null;
  }

  confirmDeleteFaq() {
    if (!this.faqGroupToDelete || this.faqIndexToDelete === null) {
      return;
    }

    // Reuse your existing logic
    this.deleteFaq(this.faqGroupToDelete, this.faqIndexToDelete);

    // Close dialog
    this.cancelDeleteFaq();
  }

  private smoothScrollTo(element: HTMLElement, duration = 600) {
    const targetY = element.getBoundingClientRect().top + window.scrollY - 80;
    const startY = window.scrollY;
    const distance = targetY - startY;
    const startTime = performance.now();

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);

      window.scrollTo(0, startY + distance * eased);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }
}
